export const FACTORY_VISIT_SESSION_REUSE_DISABLED = -1;
export const DEFAULT_FACTORY_VISIT_SESSION_REUSE_DAYS = 1;
export const DEFAULT_INSPECTOR_TIMEZONE = 'UTC';

const CALENDAR_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const MILLISECONDS_PER_DAY = 86_400_000;

const localDateFormatterCache = new Map<string, Intl.DateTimeFormat>();

export type ReusableSessionCandidate = {
  sessionDate: string;
  updatedAt?: string | null;
  evidence: Array<{ role: string }>;
};

export function parseFactoryVisitSessionReuseDays(raw: unknown): number {
  if (raw === null || raw === undefined || raw === '') {
    return DEFAULT_FACTORY_VISIT_SESSION_REUSE_DAYS;
  }
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    const value = Math.trunc(raw);
    if (value === 0) {
      return DEFAULT_FACTORY_VISIT_SESSION_REUSE_DAYS;
    }
    return value;
  }
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) {
      return DEFAULT_FACTORY_VISIT_SESSION_REUSE_DAYS;
    }
    const parsed = Number.parseInt(trimmed, 10);
    if (Number.isFinite(parsed)) {
      if (parsed === 0) {
        return DEFAULT_FACTORY_VISIT_SESSION_REUSE_DAYS;
      }
      return parsed;
    }
  }
  return DEFAULT_FACTORY_VISIT_SESSION_REUSE_DAYS;
}

export function isFactoryVisitSessionReuseEnabled(reuseDays: number): boolean {
  return reuseDays >= 0;
}

export function isValidIanaTimeZone(timeZone: string): boolean {
  if (!timeZone.trim()) {
    return false;
  }
  try {
    Intl.DateTimeFormat('en-US', { timeZone }).format(new Date());
    return true;
  } catch {
    return false;
  }
}

export function toUtcDateString(date: Date = new Date()): string {
  return date.toISOString().slice(0, 10);
}

function getLocalDateFormatter(timeZone: string): Intl.DateTimeFormat {
  let formatter = localDateFormatterCache.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-CA-u-nu-latn', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    localDateFormatterCache.set(timeZone, formatter);
  }
  return formatter;
}

export function toLocalDateString(timeZone: string, date: Date = new Date()): string {
  const resolvedTimeZone = isValidIanaTimeZone(timeZone) ? timeZone : DEFAULT_INSPECTOR_TIMEZONE;
  const parts = getLocalDateFormatter(resolvedTimeZone).formatToParts(date);
  const map = Object.fromEntries(parts.map((part) => [part.type, part.value])) as Record<
    string,
    string
  >;
  return `${map.year}-${map.month}-${map.day}`;
}

export function shiftLocalCalendarDays(
  dateStr: string,
  deltaDays: number,
  timeZone: string
): string {
  const [year, month, day] = dateStr.split('-').map((value) => Number.parseInt(value, 10));
  const utcNoon = Date.UTC(year, month - 1, day, 12, 0, 0);
  const adjusted = new Date(utcNoon + deltaDays * MILLISECONDS_PER_DAY);
  return toLocalDateString(timeZone, adjusted);
}

export function getReuseWindowStartDate(
  reuseDays: number,
  timeZone: string,
  now: Date = new Date()
): string {
  const resolvedTimeZone = isValidIanaTimeZone(timeZone) ? timeZone : DEFAULT_INSPECTOR_TIMEZONE;
  if (!isFactoryVisitSessionReuseEnabled(reuseDays) || reuseDays < 1) {
    return toLocalDateString(resolvedTimeZone, now);
  }
  const windowEnd = toLocalDateString(resolvedTimeZone, now);
  return shiftLocalCalendarDays(windowEnd, -(reuseDays - 1), resolvedTimeZone);
}

export function isSessionDateInReuseWindow(
  sessionDate: string,
  reuseDays: number,
  timeZone: string,
  now: Date = new Date()
): boolean {
  if (!CALENDAR_DATE_PATTERN.test(sessionDate) || !isFactoryVisitSessionReuseEnabled(reuseDays)) {
    return false;
  }
  const resolvedTimeZone = isValidIanaTimeZone(timeZone) ? timeZone : DEFAULT_INSPECTOR_TIMEZONE;
  const windowStart = getReuseWindowStartDate(reuseDays, resolvedTimeZone, now);
  const windowEnd = toLocalDateString(resolvedTimeZone, now);
  return sessionDate >= windowStart && sessionDate <= windowEnd;
}

function sessionCompletenessScore(evidence: Array<{ role: string }>): number {
  const hasEntrance = evidence.some((item) => item.role === 'ENTRANCE');
  const hasRoom = evidence.some((item) => item.role === 'INSPECTION_ROOM');
  if (hasEntrance && hasRoom) {
    return 2;
  }
  if (hasEntrance || hasRoom) {
    return 1;
  }
  return 0;
}

function toUpdatedAtMs(updatedAt: string | null | undefined): number {
  if (!updatedAt) {
    return 0;
  }
  const ms = new Date(updatedAt).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

export function pickBestReusableSession<T extends ReusableSessionCandidate>(
  sessions: T[]
): T | null {
  if (sessions.length === 0) {
    return null;
  }
  const ranked = [...sessions].sort((left, right) => {
    const completenessDelta =
      sessionCompletenessScore(right.evidence) - sessionCompletenessScore(left.evidence);
    if (completenessDelta !== 0) {
      return completenessDelta;
    }
    const sessionDateDelta = right.sessionDate.localeCompare(left.sessionDate);
    if (sessionDateDelta !== 0) {
      return sessionDateDelta;
    }
    return toUpdatedAtMs(right.updatedAt) - toUpdatedAtMs(left.updatedAt);
  });
  const best = ranked[0];
  if (sessionCompletenessScore(best.evidence) === 0) {
    return null;
  }
  return best;
}
